import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, CheckCircle2, Globe, KeyRound, Loader2, Lock, RefreshCw,
  ShieldCheck, Trash2, Unlink,
} from 'lucide-react';
import client from '../lib/api';
import { useToast } from '../hooks/use-toast';

// Anahtar biçimi lisans sunucusundaki desenle aynıdır; yanlış yazılmış bir
// anahtar için sunucuya hiç gidilmez.
const KEY_PATTERN = /^PT-[A-Z0-9]{5}-[A-Z0-9]{5}-[A-Z0-9]{5}-[A-Z0-9]{5}$/;

// Durum rozetinin tonu: kısıt uygulanan durum kırmızı, uyarılar sarı.
const STATUS_TONE = {
  active: 'ok',
  grace: 'warn',
  unreachable: 'warn',
  pending: 'warn',
  unconfigured: 'muted',
  invalid: 'danger',
};

const formatDate = (value) => (
  value ? new Date(value).toLocaleString('tr-TR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }) : '—');

const formatDay = (value) => (
  value ? new Date(value).toLocaleDateString('tr-TR', {
    day: '2-digit', month: 'long', year: 'numeric',
  }) : 'Süresiz');

/** Bitişe kalan gün — süresiz lisansta boş döner. */
const daysLeft = (value) => {
  if (!value) return null;
  return Math.ceil((new Date(value).getTime() - Date.now()) / 86400000);
};

const Stat = ({ label, value, hint, tone }) => (
  <div className={`admin-license-stat${tone ? ` tone-${tone}` : ''}`}>
    <span className="admin-license-stat-label">{label}</span>
    <span className="admin-license-stat-value">{value}</span>
    {hint && <span className="admin-license-stat-hint">{hint}</span>}
  </div>
);

const License = () => {
  const [license, setLicense] = useState(null);
  const [form, setForm] = useState({ license_key: '', api_url: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const { toast } = useToast();

  const apply = useCallback((data) => {
    setLicense(data);
    setForm({
      license_key: data.license_key || '',
      api_url: data.api_url || '',
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await client.get('/admin/license');
      apply(data);
    } catch (requestError) {
      setError(
        requestError.response?.data?.detail || 'Lisans bilgisi alınamadı');
    } finally {
      setLoading(false);
    }
  }, [apply]);

  useEffect(() => { load(); }, [load]);

  const keyValid = useMemo(
    () => KEY_PATTERN.test((form.license_key || '').trim().toUpperCase()),
    [form.license_key],
  );

  const run = async (name, action, successTitle) => {
    setBusy(name);
    try {
      const data = await action();
      apply(data.license || data);
      if (successTitle) {
        toast({
          title: data.ok === false ? 'İşlem tamamlanamadı' : successTitle,
          description: data.message || '',
        });
      }
      return true;
    } catch (requestError) {
      // Doğrulanmayan anahtar kaydedilmez; sunucunun gerekçesi olduğu gibi
      // gösterilir (domain eşleşmedi, koltuk doldu, süre bitti…).
      toast({
        title: 'İşlem tamamlanamadı',
        description: requestError.response?.data?.detail
          || 'Lisans sunucusuna ulaşılamadı',
      });
      return false;
    } finally {
      setBusy('');
    }
  };

  const save = async (event) => {
    event.preventDefault();
    if (!keyValid) {
      toast({
        title: 'Anahtar biçimi geçersiz',
        description: 'Beklenen biçim: PT-XXXXX-XXXXX-XXXXX-XXXXX',
      });
      return;
    }
    const ok = await run('save', async () => {
      const { data } = await client.put('/admin/license', {
        license_key: form.license_key.trim().toUpperCase(),
        api_url: form.api_url.trim(),
      });
      return data;
    });
    if (ok) toast({ title: 'Lisans doğrulandı ve kaydedildi' });
  };

  const check = () => run(
    'check',
    async () => (await client.post('/admin/license/check')).data,
    'Lisans geçerli',
  );

  const release = () => {
    if (!window.confirm(
      'Bu kurulumun aktivasyonu bırakılsın mı? Lisans anahtarı kayıtta kalır '
      + 've bir sonraki kontrolde kurulum yeniden aktive edilir.')) return null;
    return run(
      'release',
      async () => (await client.post('/admin/license/release')).data,
      'Aktivasyon bırakıldı',
    );
  };

  const remove = () => {
    if (!window.confirm(
      'Lisans bu kurulumdan kaldırılsın mı? Anahtar silinir ve kurulum '
      + 'koltuğu iade edilir.')) return null;
    return run(
      'remove',
      async () => (await client.delete('/admin/license')).data,
      'Lisans kaldırıldı',
    );
  };

  // Tanımlı lisansın üzerine yazılmaz: değiştirmenin yolu önce kaldırmaktır
  // (backend de aynı kuralı uygular, aksi hâlde eski aktivasyon lisans
  // sunucusunda açık kalır ve kurulum hakkı boşuna dolardı).
  const locked = !!license?.configured;
  // Muaf kurulum (anahtarsız, kısıtsız) panelde geçerli lisans gibi çizilir;
  // muafiyet duyurulmaz.
  const exempt = !!license && !license.configured && !license.restricted
    && license.required === false;
  const tone = exempt ? STATUS_TONE.active : (STATUS_TONE[license?.status] || 'muted');
  const remaining = daysLeft(license?.expires_at);
  const movedDomain = license
    && license.detected_domain
    && license.domain
    && license.detected_domain !== license.domain;

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1>Lisans</h1>
          <p>
            Bu kurulumun lisans anahtarı ve geçerlilik bilgisi. Alan adı
            otomatik algılanır, doğrulama arka planda düzenli olarak yenilenir.
          </p>
        </div>
        {license?.configured && (
          <button
            type="button"
            className="admin-btn admin-btn-secondary"
            onClick={check}
            disabled={!!busy}
          >
            {busy === 'check'
              ? <Loader2 size={16} className="animate-spin" />
              : <RefreshCw size={16} />}
            Şimdi Doğrula
          </button>
        )}
      </div>

      {error && (
        <div className="admin-card admin-state" role="alert">
          <AlertTriangle size={26} className="text-amber-400" />
          <h2>{error}</h2>
          <button type="button" className="admin-btn admin-btn-secondary" onClick={load}>
            Yeniden dene
          </button>
        </div>
      )}

      {loading && !license && (
        <div className="admin-card admin-state">
          <Loader2 size={26} className="animate-spin text-slate-400" />
          <h2>Lisans bilgisi yükleniyor…</h2>
        </div>
      )}

      {license && !error && (
        <>
          <div className="admin-card admin-license-card">
            <div className={`admin-license-hero tone-${tone}`}>
              <span className="admin-license-hero-icon">
                {(license.status === 'active' || exempt)
                  ? <ShieldCheck size={22} />
                  : <AlertTriangle size={22} />}
              </span>
              <div className="admin-license-hero-copy">
                <div className="admin-license-hero-title">
                  <strong>{exempt ? 'Aktif' : license.status_label}</strong>
                  {license.product && <span>{license.product}</span>}
                </div>
                <p>
                  {license.restricted
                    ? 'Kısıt etkin: oyun açma, bahis ve para yatırma durdu. '
                      + 'Üye girişi, bakiye ve çekim talebi çalışmaya devam ediyor.'
                    : exempt
                    ? 'Lisans geçerli, kısıt uygulanmıyor.'
                    : (license.message
                      || (license.configured
                        ? 'Lisans geçerli, kısıt uygulanmıyor.'
                        : 'Anahtar tanımlanana kadar kurulum kısıtlı çalışır.'))}
                </p>
              </div>
            </div>

            {license.configured && (
              <div className="admin-license-stats">
                <Stat
                  label="Bitiş tarihi"
                  value={formatDay(license.expires_at)}
                  hint={remaining !== null && remaining >= 0
                    ? `${remaining} gün kaldı` : undefined}
                  tone={remaining !== null && remaining <= 7 ? 'warn' : undefined}
                />
                {license.grace_ends_at && (
                  <Stat label="Ek süre sonu" value={formatDay(license.grace_ends_at)} />
                )}
                <Stat
                  label="Aktivasyon"
                  value={license.activated ? 'Bu kuruluma tanımlı' : 'Tanımlı değil'}
                />
                <Stat
                  label="Son başarılı doğrulama"
                  value={formatDate(license.last_success_at)}
                />
                <Stat
                  label="Sıradaki kontrol"
                  value={formatDate(license.next_check_at)}
                />
                {license.status === 'unreachable' && (
                  <Stat
                    label="Çevrimdışı çalışma sonu"
                    value={formatDate(license.offline_grace_until)}
                    tone="warn"
                  />
                )}
                {(license.features || []).length > 0 && (
                  <Stat
                    label="Özellikler"
                    value={(license.features || []).join(', ')}
                  />
                )}
              </div>
            )}

            <div className="admin-license-meta">
              <div>
                <span className="admin-license-stat-label">
                  <Globe size={12} aria-hidden="true" /> Alan adı (otomatik)
                </span>
                <code>{license.detected_domain || license.domain || '—'}</code>
              </div>
              <div>
                <span className="admin-license-stat-label">Kurulum kimliği</span>
                <code>{license.instance_id || '—'}</code>
              </div>
            </div>

            {movedDomain && (
              <div className="admin-inline-warning">
                <AlertTriangle size={15} />
                <div>
                  <strong>Kurulum başka bir alan adından açılıyor.</strong>
                  {' '}
                  Lisans <code>{license.domain}</code> adresine kayıtlı, site şu
                  anda <code>{license.detected_domain}</code> adresinden
                  açılıyor. Bir sonraki doğrulama yeni adresle yapılır; lisansta
                  bu alan adı tanımlı değilse kısıt devreye girer.
                </div>
              </div>
            )}

            {license.last_message && license.status !== 'active' && (
              <div className="admin-inline-warning">
                <AlertTriangle size={15} />
                <div>{license.last_message}</div>
              </div>
            )}
          </div>

          <form className="admin-card admin-license-card" onSubmit={save}>
            <div className="admin-license-form-head">
              <h2>Lisans Anahtarı</h2>
              <p>
                {locked
                  ? 'Bu kurulumun lisansı tanımlı. Anahtarı ya da sunucu '
                    + 'adresini değiştirmek için önce "Lisansı Kaldır" deyin — '
                    + 'kurulum koltuğu lisans sunucusuna iade edilir.'
                  : 'Anahtar kaydedilmeden önce lisans sunucusunda doğrulanır; '
                    + 'doğrulanmayan anahtar kaydedilmez.'}
              </p>
            </div>

            <div className="admin-license-form-grid">
              <label className="admin-license-field">
                <span className="admin-license-stat-label">
                  Lisans anahtarı
                  {locked && <Lock size={11} aria-hidden="true" />}
                </span>
                <input
                  className="admin-input"
                  value={form.license_key}
                  placeholder="PT-XXXXX-XXXXX-XXXXX-XXXXX"
                  autoComplete="off"
                  spellCheck={false}
                  readOnly={locked}
                  onChange={(event) => setForm((prev) => ({
                    ...prev, license_key: event.target.value.toUpperCase(),
                  }))}
                />
                {!locked && form.license_key && !keyValid && (
                  <span className="admin-license-field-error">
                    Biçim: PT-XXXXX-XXXXX-XXXXX-XXXXX
                  </span>
                )}
              </label>

              <label className="admin-license-field">
                <span className="admin-license-stat-label">
                  Lisans sunucusu adresi
                  {locked && <Lock size={11} aria-hidden="true" />}
                </span>
                <input
                  className="admin-input"
                  value={form.api_url}
                  placeholder="https://panel.ornek.com"
                  autoComplete="off"
                  spellCheck={false}
                  readOnly={locked}
                  onChange={(event) => setForm((prev) => ({
                    ...prev, api_url: event.target.value,
                  }))}
                />
              </label>
            </div>

            <div className="admin-license-actions">
              {!locked && (
                <button
                  type="submit"
                  className="admin-btn admin-btn-primary"
                  disabled={!!busy}
                >
                  {busy === 'save'
                    ? <Loader2 size={16} className="animate-spin" />
                    : <KeyRound size={16} />}
                  Doğrula ve Kaydet
                </button>
              )}
              {/* Aktivasyon bırakma yalnızca gerçekten çözdüğü durumda
                  görünür (aktivasyon başka bir domaine bağlı kalmışsa);
                  sıradan koltuk iadesi zaten "Lisansı Kaldır" ile yapılır. */}
              {license.needs_activation_reset && (
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary"
                  onClick={release}
                  disabled={!!busy}
                >
                  {busy === 'release'
                    ? <Loader2 size={16} className="animate-spin" />
                    : <Unlink size={16} />}
                  Aktivasyonu Sıfırla
                </button>
              )}
              {locked && (
                <button
                  type="button"
                  className="admin-btn admin-btn-danger"
                  onClick={remove}
                  disabled={!!busy}
                >
                  {busy === 'remove'
                    ? <Loader2 size={16} className="animate-spin" />
                    : <Trash2 size={16} />}
                  Lisansı Kaldır
                </button>
              )}
            </div>

            <p className="admin-license-note">
              <CheckCircle2 size={14} aria-hidden="true" />
              <span>
                Sunucuya ulaşılamadığında kurulum, lisans sunucusunun bildirdiği
                çevrimdışı çalışma süresi boyunca kısıtsız çalışır. Lisans
                geçersizleştiğinde oyun açma, bahis ve para yatırma durur; üye
                girişi, bakiye görüntüleme ve çekim talebi açık kalır.
              </span>
            </p>
          </form>
        </>
      )}
    </div>
  );
};

export default License;
